/**
 * Image Service
 * 
 * Manages loading and accessing adventure title images and banners from the image server.
 * Uses lightweight ID list endpoints for efficient loading.
 */

import { imageServerConfig } from '../config/imageServer';

export type ImageVariantSize = 'thumbnail' | 'card' | 'large';

export interface ImageUrls {
  webp: string;
  jpg: string;
}

export class ImageService {
  private static instance: ImageService;
  private titleImageIds: string[] = [];
  private bannerIds: string[] = [];
  private titleImageIdsLoaded = false;
  private bannerIdsLoaded = false;
  private isLoading: boolean = false;
  private loadPromise: Promise<void> | null = null;

  private constructor() {}

  /**
   * Get the singleton instance of ImageService
   */
  public static getInstance(): ImageService {
    if (!ImageService.instance) {
      ImageService.instance = new ImageService();
    }
    return ImageService.instance;
  }

  /**
   * Load available image IDs from the image server
   * Fetches portrait, title-image, and banner ID lists in parallel
   * @returns Promise that resolves when all IDs are loaded
   */
  public async loadAvailableIds(): Promise<void> {
    if (this.titleImageIdsLoaded && this.bannerIdsLoaded) {
      return Promise.resolve();
    }

    if (this.isLoading && this.loadPromise) {
      return this.loadPromise;
    }

    this.isLoading = true;
    this.loadPromise = this.fetchIds();

    try {
      await this.loadPromise;
    } finally {
      this.isLoading = false;
      this.loadPromise = null;
    }
  }

  /**
   * Force-refresh ID lists (e.g. after image load failures while adventures register).
   */
  public async refreshAvailableIds(): Promise<void> {
    this.titleImageIdsLoaded = false;
    this.bannerIdsLoaded = false;
    this.titleImageIds = [];
    this.bannerIds = [];
    await this.loadAvailableIds();
  }

  /**
   * Fetch ID lists from the server in parallel
   */
  private async fetchIds(): Promise<void> {
    try {
      const [titleImagesResponse, bannersResponse] = await Promise.all([
        fetch(imageServerConfig.titleImagesListUrl),
        fetch(imageServerConfig.bannersListUrl)
      ]);

      if (titleImagesResponse.ok) {
        this.titleImageIds = await titleImagesResponse.json();
        this.titleImageIdsLoaded = true;
        console.log(`Title images loaded: ${this.titleImageIds.length} available`);
      } else {
        console.warn(`Failed to load title image IDs: ${titleImagesResponse.status} ${titleImagesResponse.statusText}`);
      }

      if (bannersResponse.ok) {
        this.bannerIds = await bannersResponse.json();
        this.bannerIdsLoaded = true;
        console.log(`Banners loaded: ${this.bannerIds.length} available`);
      } else {
        console.warn(`Failed to load banner IDs: ${bannersResponse.status} ${bannersResponse.statusText}`);
      }
    } catch (error) {
      console.warn('Failed to load image IDs:', error);
      // Continue gracefully - images will fall back to generic
    }
  }

  /**
   * Get the URLs for a title image (WebP and JPEG)
   * @param titleImageId - The ID of the title image (defaults to 'generic' if not provided)
   * @param variant - The variant size to retrieve
   * @returns Object with webp and jpg URLs
   */
  public getTitleImageUrls(titleImageId: string | undefined, variant: ImageVariantSize): ImageUrls {
    const id = titleImageId || 'generic';
    const baseUrl = imageServerConfig.getTitleImageUrl(id, variant);
    
    // For now, construct URLs by appending format query params or relying on Accept headers
    // Since we're using smart format negotiation, we return the same base URL
    // The actual format will be negotiated by the server based on Accept header
    return {
      webp: baseUrl,
      jpg: baseUrl,
    };
  }

  /**
   * Get the URLs for a banner (WebP and JPEG)
   * @param bannerId - The ID of the banner (defaults to 'generic' if not provided)
   * @param variant - The variant size to retrieve
   * @returns Object with webp and jpg URLs
   */
  public getBannerUrls(bannerId: string | undefined, variant: ImageVariantSize): ImageUrls {
    const id = bannerId || 'generic';
    const baseUrl = imageServerConfig.getBannerUrl(id, variant);
    
    return {
      webp: baseUrl,
      jpg: baseUrl,
    };
  }

  /**
   * Get the URL for a title image with smart format negotiation (legacy method)
   * @deprecated Use getTitleImageUrls instead for consistency with picture element
   */
  public getTitleImageUrl(titleImageId: string | undefined, variant: ImageVariantSize): string {
    return imageServerConfig.getTitleImageUrl(titleImageId || 'generic', variant);
  }

  /**
   * Get the URL for a banner with smart format negotiation (legacy method)
   * @deprecated Use getBannerUrls instead for consistency with picture element
   */
  public getBannerUrl(bannerId: string | undefined, variant: ImageVariantSize): string {
    return imageServerConfig.getBannerUrl(bannerId || 'generic', variant);
  }

  /**
   * Get a list of all available title image IDs
   * @returns Array of title image IDs
   */
  public getTitleImageIds(): string[] {
    return [...this.titleImageIds];
  }

  /**
   * Get a list of all available banner IDs
   * @returns Array of banner IDs
   */
  public getBannerIds(): string[] {
    return [...this.bannerIds];
  }

  /**
   * Check if the IDs have been loaded
   * @returns True if the IDs are loaded
   */
  public isLoaded(): boolean {
    return this.titleImageIdsLoaded && this.bannerIdsLoaded;
  }

  /**
   * Check if a title image exists
   * @param titleImageId - The ID to check
   * @returns True if the title image exists
   */
  public hasTitleImage(titleImageId: string): boolean {
    return this.titleImageIds.includes(titleImageId);
  }

  /**
   * Check if a banner exists
   * @param bannerId - The ID to check
   * @returns True if the banner exists
   */
  public hasBanner(bannerId: string): boolean {
    return this.bannerIds.includes(bannerId);
  }

  /**
   * Reset the service (useful for testing)
   */
  public reset(): void {
    this.titleImageIds = [];
    this.bannerIds = [];
    this.titleImageIdsLoaded = false;
    this.bannerIdsLoaded = false;
    this.isLoading = false;
    this.loadPromise = null;
  }
}

// Export singleton instance
export const imageService = ImageService.getInstance();
export default imageService;

